import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NewShareTemplate } from '@hero/components';
import { ShareTable } from '@hero/components/organisms';
import { PageLayout } from '@hero/layouts';
import { OnboardingCard } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetRecognition, useGetRecognitionOrders } from '@hero/services/order-service';
import { useOnboardingStore } from '@hero/store/hooks';

function NewRecognitionShare() {
  const { recognitionCode } = useParams();
  const { t } = useTranslation();
  const [globalFilters, setGlobalFilters] = useState({});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const { data: recognition } = useGetRecognition({ recognitionCode });
  const { recognition: onboardingRecognition } = useOnboardingStore();

  const [isOnboardingCardShare, setIsOnboardingCardShare] = useState(false);

  const navigate = useNavigate();

  const params = useMemo(
    () => ({
      filters: {
        searchTerm: globalFilters?.searchTerms,
        giftStatus: globalFilters?.filters?.map(filter => filter.value),
        recognitionId: recognition?.id
      },
      pagination: {
        limit: pageSize,
        page: pageIndex + 1
      }
    }),
    [pageSize, pageIndex, recognition?.id, globalFilters]
  );

  const {
    data: recipientsData,
    isFetching: isLoadingRecipients,
    refetch: refetchRecipients
  } = useGetRecognitionOrders(params, {
    retryOnMount: false,
    enabled: !!recognition?.id
  });

  const shareType = recognition?.contacts?.type === 'list' ? 'table' : 'share';

  return (
    <PageLayout
      disableClickToCompleted
      header={{
        title: t('oneTimeGiftReceived'),
        backButton: () => navigate(ROUTE_PATHS.MY_RECOGNITION_DETAIL(recognitionCode))
      }}
      navigation={{
        leftButton: {
          children: t('goToName', { name: t('recognitions') }),
          onClick: () => navigate(ROUTE_PATHS.MY_RECOGNITIONS)
        }
      }}
    >
      <OnboardingCard
        styles={{
          wrapper: {
            right: 200,
            top: 50
          }
        }}
        position="left-start"
        cardOffset={[20, -25]}
        isCardShown={isOnboardingCardShare}
        isInfoActive={onboardingRecognition.infoCardsActive}
        setIsCardShown={setIsOnboardingCardShare}
        onTourButtonClick={() => setIsOnboardingCardShare(false)}
        title={t('giftLinks')}
        description={t('giftLinksOnboardingCardDesc')}
        featureType="recognition"
      />
      {shareType === 'table' ? (
        <ShareTable
          data={recipientsData?.docs}
          globalFilters={globalFilters}
          setGlobalFilters={setGlobalFilters}
          pagination={{
            pageIndex,
            pageSize,
            total: recipientsData?.totalDocs
          }}
          isLoading={isLoadingRecipients}
          pageCount={recipientsData?.totalPages}
          setPagination={setPagination}
          refetchRecognition={refetchRecipients}
        />
      ) : (
        <NewShareTemplate
          name={recognition?.name}
          endDate={recognition?.settings?.expiresAt}
          recognitionCode={recognitionCode}
          recipientCount={recognition?.contacts?.maxCount}
          featureType="recognition"
          secureSingleLink={recognition?.link?.shortened}
          recipientVerificationType={recognition?.settings?.recipientVerification?.type}
        />
      )}
    </PageLayout>
  );
}

export { NewRecognitionShare };
